import { Link } from "@tanstack/react-router";
import { AlarmClockOff, Archive, ArchiveRestore, Clock, Mail, MailOpen } from "lucide-react";
import { useId, type AnchorHTMLAttributes, type KeyboardEvent, type ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { OPCOES_DE_ADIAR, adiadaAgora, quandoVolta, type AdiarAte } from "@/lib/notificacoes/caixa";
import { aberturaDoAviso } from "@/lib/notificacoes/destino";
import { grupoDaData, type GrupoDaData } from "@/lib/notificacoes/grupo-da-data";
import { oQueFazer } from "@/lib/notificacoes/o-que-fazer";
import type { NotificationItem } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * TUT-586a — as peças da lista de avisos, as MESMAS no painel do sino e na
 * página `/notificacoes` (prévia "A · Lista calma", escolhida pelo dono em
 * 02/11): grupos por dia; o não lido se distingue pelo peso do título e por
 * um ponto, e não mais pela borda de 3px; as ações aparecem ao passar o mouse
 * e ao focar a linha pelo teclado (no toque, ficam sempre à vista).
 */

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.round(hours / 24);
  return `há ${days} d`;
}

const ORDEM_DOS_GRUPOS: readonly GrupoDaData[] = ["Hoje", "Ontem", "Esta semana", "Anteriores"];

/** A lista inteira, em grupos de dia; cada linha é desenhada por quem chama. */
export function GruposPorDia({
  items,
  renderLinha,
}: {
  items: readonly NotificationItem[];
  renderLinha: (item: NotificationItem) => ReactNode;
}) {
  const prefixo = useId();
  const agora = new Date();
  return (
    <div data-lista-de-avisos className="flex flex-col gap-2">
      {ORDEM_DOS_GRUPOS.map((grupo, indice) => {
        const itens = items.filter((item) => grupoDaData(item.createdAt, agora) === grupo);
        if (itens.length === 0) return null;
        const id = `${prefixo}-grupo-${indice}`;
        return (
          <section key={grupo} aria-labelledby={id}>
            <h3
              id={id}
              className="m-0 px-2 pb-1 pt-1.5 text-legenda font-bold uppercase tracking-wide text-[var(--muted)]"
            >
              {grupo}
            </h3>
            <ul className="m-0 grid list-none gap-px p-0">{itens.map(renderLinha)}</ul>
          </section>
        );
      })}
    </div>
  );
}

/**
 * As setas (e o j/k da prévia) andam entre as linhas da lista; Home e End vão
 * às pontas. Fica no ALVO de cada linha (o link ou o botão), e não na lista:
 * ouvinte de teclado em elemento não interativo é o que o jsx-a11y recusa.
 */
export function aoTeclarNaLista(evento: KeyboardEvent<HTMLElement>) {
  if (evento.ctrlKey || evento.metaKey || evento.altKey) return;
  const passo = { ArrowDown: 1, j: 1, ArrowUp: -1, k: -1, Home: -Infinity, End: Infinity }[evento.key];
  if (passo === undefined) return;
  const lista = evento.currentTarget.closest("[data-lista-de-avisos]");
  const alvos = Array.from(lista?.querySelectorAll<HTMLElement>("[data-alvo-do-aviso]") ?? []);
  const atual = alvos.indexOf(evento.currentTarget);
  const proximo = Math.max(0, Math.min(alvos.length - 1, atual + passo));
  evento.preventDefault();
  alvos[proximo]?.focus();
}

/**
 * O alvo principal da linha (o título): o `::after` cobre a linha inteira, e
 * clicar em qualquer ponto dela é clicar nele. As ações ficam por cima (z-10).
 */
export const classeDoAlvo =
  "min-w-0 rounded-[6px] text-left text-foreground no-underline outline-none after:absolute after:inset-0 after:rounded-[10px] after:content-[''] focus-visible:after:ring-2 focus-visible:after:ring-ring";

/** O título com o "Não lida." que o leitor de tela diz no lugar do ponto. */
export function TituloDoAviso({ item }: { item: NotificationItem }) {
  return (
    <>
      {/* O espaço fica FORA do `sr-only` (TUT-238): dentro dele, o nome
          acessível sai "Não lida.Prazo vencido". */}
      {item.read ? null : (
        <>
          <span className="sr-only">Não lida.</span>{" "}
        </>
      )}
      {item.title}
    </>
  );
}

/**
 * A ação "Abrir" do aviso como link (o painel do sino): o processo na aba do
 * assunto, a conversa, a tela da entidade ou o detalhe na central.
 */
export function LinkDoAviso({
  item,
  children,
  ...comum
}: { item: NotificationItem; children: ReactNode } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children">) {
  const abertura = aberturaDoAviso(item);
  switch (abertura.tipo) {
    case "conversa":
      return (
        <Link to="/conversas" search={{ c: abertura.c }} {...comum}>
          {children}
        </Link>
      );
    case "processo":
      return (
        <Link
          to="/processos/$processId"
          params={{ processId: abertura.processId }}
          search={{ aba: abertura.aba, ...(abertura.laudo ? { laudo: abertura.laudo } : {}) }}
          {...comum}
        >
          {children}
        </Link>
      );
    case "tela":
      return (
        <Link to={abertura.to} {...comum}>
          {children}
        </Link>
      );
    default:
      // Em "Todas": abrir marca como lido, e em "Não lidas" (o padrão da
      // página) o aviso não estaria na lista para abrir no detalhe.
      return (
        <Link to="/notificacoes" search={{ filtro: "todas", id: abertura.id }} {...comum}>
          {children}
        </Link>
      );
  }
}

/** Uma linha da lista: ponto, título (o alvo), hora, processo, o texto e as ações. */
export function LinhaDoAviso({
  item,
  alvo,
  acoes,
  marca,
  escolhida = false,
}: {
  item: NotificationItem;
  /** O título já dentro do link ou do botão, com `classeDoAlvo`. */
  alvo: ReactNode;
  acoes: ReactNode;
  /** Uma marca a mais embaixo do texto (o Tipo, na página). */
  marca?: ReactNode;
  escolhida?: boolean;
}) {
  const fazer = oQueFazer(item.eventKey);
  return (
    <li
      className={cn(
        "group relative grid grid-cols-[0.75rem_minmax(0,1fr)] gap-x-2 rounded-[10px] px-2.5 py-2",
        "hover:bg-surface-2 focus-within:bg-surface-2",
        escolhida && "bg-surface-2 ring-1 ring-[var(--border-strong)]",
      )}
    >
      <span
        aria-hidden="true"
        className={cn("mt-[0.4rem] size-2 rounded-full", !item.read && "bg-primary")}
      />
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <strong className={cn("min-w-0", item.read ? "font-medium" : "font-semibold")}>{alvo}</strong>
          <small className="shrink-0 whitespace-nowrap text-legenda text-[var(--muted)] group-focus-within:invisible group-hover:invisible">
            {relativeTime(item.createdAt)}
          </small>
        </div>
        {item.processo ? (
          <span className="block truncate text-legenda text-[var(--muted)]">
            {item.processo.numero} · {item.processo.partes}
          </span>
        ) : null}
        <p className="m-0 mt-0.5 text-legenda text-[var(--muted)]">
          {item.body}
          {fazer ? (
            <>
              {" "}
              <span className={cn(!item.read && "font-medium text-foreground")}>{fazer}</span>
            </>
          ) : null}
        </p>
        <MarcaDeAdiada item={item} />
        {marca}
      </div>
      {/* No mouse, as ações flutuam no canto e aparecem no hover e no foco;
          no toque (sem hover), ficam no fluxo, sempre à vista. */}
      <fieldset
        aria-label={`Ações do aviso ${item.title}`}
        className={cn(
          // A moldura vale nos dois modos; só a posição e a visibilidade mudam
          // (cada variante `pointer-*` é uma regra a mais no CSS de entrada).
          "z-10 m-0 flex w-fit min-w-0 gap-px rounded-[10px] border border-border bg-[var(--surface)] p-0.5 shadow-sm right-1.5 top-1",
          "pointer-fine:pointer-events-none pointer-fine:absolute pointer-fine:opacity-0",
          "pointer-fine:group-hover:pointer-events-auto pointer-fine:group-hover:opacity-100 pointer-fine:group-focus-within:pointer-events-auto pointer-fine:group-focus-within:opacity-100",
          // Com o menu de Adiar aberto, o foco está no menu (fora da linha): a barra fica.
          "pointer-fine:has-data-[state=open]:opacity-100",
          "pointer-coarse:col-start-2 pointer-coarse:mt-1.5",
        )}
      >
        {acoes}
      </fieldset>
    </li>
  );
}

/** O que as ações da linha fazem (o `useAcoesDoAviso`). */
export interface AcoesDaLinha {
  ocupado: boolean;
  alternarLida: (item: NotificationItem) => void;
  adiar: (item: NotificationItem, ate: AdiarAte) => void;
  reativar: (item: NotificationItem) => void;
  arquivar: (item: NotificationItem) => void;
  restaurar: (item: NotificationItem) => void;
}

/**
 * As ações da linha: Lida/Não lida (586a), Adiar (1 hora, Amanhã, Próxima
 * semana) ou Reativar o adiado, Arquivar ou Restaurar o arquivado (586b).
 * O arquivado não se adia: primeiro volta do arquivo.
 */
export function AcoesDoAviso({ item, acoes }: { item: NotificationItem; acoes: AcoesDaLinha }) {
  const arquivado = Boolean(item.archivedAt);
  const adiado = adiadaAgora(item);
  return (
    <>
      <IconButton
        tamanho="sm"
        label={item.read ? "Marcar como não lida" : "Marcar como lida"}
        dica={item.read ? "Não lida" : "Lida"}
        icone={item.read ? <Mail aria-hidden="true" /> : <MailOpen aria-hidden="true" />}
        disabled={acoes.ocupado}
        onClick={() => acoes.alternarLida(item)}
      />
      {arquivado ? null : adiado ? (
        <IconButton
          tamanho="sm"
          label="Reativar agora"
          dica="Reativar"
          icone={<AlarmClockOff aria-hidden="true" />}
          disabled={acoes.ocupado}
          onClick={() => acoes.reativar(item)}
        />
      ) : (
        <MenuDeAdiar item={item} acoes={acoes}>
          <IconButton tamanho="sm" label="Adiar" icone={<Clock aria-hidden="true" />} disabled={acoes.ocupado} />
        </MenuDeAdiar>
      )}
      {arquivado ? (
        <IconButton
          tamanho="sm"
          label="Restaurar do arquivo"
          dica="Restaurar"
          icone={<ArchiveRestore aria-hidden="true" />}
          disabled={acoes.ocupado}
          onClick={() => acoes.restaurar(item)}
        />
      ) : (
        <IconButton
          tamanho="sm"
          label="Arquivar"
          icone={<Archive aria-hidden="true" />}
          disabled={acoes.ocupado}
          onClick={() => acoes.arquivar(item)}
        />
      )}
    </>
  );
}

/** O menu "Adiar até quando", em volta do gatilho que quem chama desenha. */
export function MenuDeAdiar({
  item,
  acoes,
  children,
}: {
  item: NotificationItem;
  acoes: AcoesDaLinha;
  children: ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" aria-label="Adiar até quando">
        {OPCOES_DE_ADIAR.map((opcao) => (
          <DropdownMenuItem key={opcao.ate} onSelect={() => acoes.adiar(item, opcao.ate)}>
            {opcao.rotulo}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "Adiada · volta seg., 09/11, 08:00" — o adiado que aparece em "Todas". */
export function MarcaDeAdiada({ item }: { item: NotificationItem }) {
  if (!item.snoozedUntil || !adiadaAgora(item)) return null;
  return (
    <span className="mr-1.5 mt-1 inline-flex rounded-full border border-dashed border-border px-2 text-legenda font-semibold text-[var(--muted)]">
      Adiada · volta {quandoVolta(item.snoozedUntil)}
    </span>
  );
}
